import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { fmtMoney } from "../lib/money";
import { LiveBadge } from "../components/Layout";
import { ChevronRight, Flame, Users, Play, Sparkles, X, Rocket, Calendar, Star, Radio } from "lucide-react";
import * as Lucide from "lucide-react";
import { useAuth } from "../lib/AuthContext";

const fmt = (n) => n >= 1000 ? (n/1000).toFixed(1)+"k" : n;

export function ProductCard({ p, index = 0 }) {
  return (
    <Link to={`/product/${p.product_id}`} data-testid={`product-card-${p.product_id}`} className="card fade-up block" style={{animationDelay: `${index*40}ms`}}>
      <div className="aspect-square bg-[#261534] relative overflow-hidden">
        {p.images?.[0] ? <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover"/> : null}
        <div className="absolute top-2 left-2 bg-black/70 text-white text-[10px] px-2 py-1 rounded font-bold uppercase tracking-widest">
          {p.store_name}
        </div>
      </div>
      <div className="p-3">
        <div className="font-semibold text-sm line-clamp-1">{p.name}</div>
        <div className="flex items-center justify-between mt-1">
          <span className="font-display font-bold text-lg text-[#D4FF00]">{fmtMoney(p.price)}</span>
          <span className="text-xs text-[#A1A1AA]">★ {p.rating?.toFixed(1) || "5.0"}</span>
        </div>
      </div>
    </Link>
  );
}

export function StoreCard({ s }) {
  return (
    <Link to={`/store/${s.store_id}`} data-testid={`store-card-${s.store_id}`} className="card block">
      <div className="h-28 relative">
        <img src={s.banner_url} alt="" className="w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent"/>
      </div>
      <div className="p-3">
        <div className="font-display font-bold text-base line-clamp-1">{s.name}</div>
        <div className="text-xs text-[#A1A1AA] mt-1 flex items-center gap-2">
          <Users size={12}/> {fmt(s.followers || 0)} followers
        </div>
      </div>
    </Link>
  );
}

export function StreamCard({ s }) {
  return (
    <Link to={`/live/${s.stream_id}`} data-testid={`stream-card-${s.stream_id}`} className="card block">
      <div className="aspect-[4/5] relative overflow-hidden">
        <img src={s.thumbnail_url} alt={s.title} className="w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent"/>
        {s.is_live && <div className="absolute top-3 left-3"><LiveBadge/></div>}
        <div className="absolute top-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
          <Users size={12}/> {fmt(s.viewers || 0)}
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          <div className="font-display font-bold text-white text-sm line-clamp-2">{s.title}</div>
          <div className="text-[11px] text-[#A1A1AA] mt-1">@{s.host_name}</div>
        </div>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-14 h-14 rounded-full bg-[#D4FF00] flex items-center justify-center opacity-0 hover:opacity-100 transition">
            <Play size={22} className="text-black fill-black"/>
          </div>
        </div>
      </div>
    </Link>
  );
}

/** Rental atelier card used in the homepage rail. Clicking deep-links
 *  into /rentals (the full discovery experience); we don't open the
 *  drawer directly to keep this card lightweight + cache-friendly. */
function RentalRailCard({ s }) {
  return (
    <Link to="/rentals" data-testid={`rental-card-${s.store_id}`}
      className="shrink-0 w-[200px] sm:w-[230px] snap-start card block group">
      <div className="aspect-[4/5] relative overflow-hidden">
        {s.cover
          ? <img src={s.cover} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition"/>
          : <div className="w-full h-full bg-[#261534]"/>}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent"/>
        <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-[#00F0FF] text-black text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
          <Calendar size={10}/> Rent
        </div>
        {s.rating ? (
          <div className="absolute top-3 right-3 bg-black/70 text-white text-[10px] px-2 py-0.5 rounded flex items-center gap-1">
            <Star size={10} className="fill-[#D4FF00] text-[#D4FF00]"/>{s.rating.toFixed(1)}
          </div>
        ) : null}
        <div className="absolute bottom-3 left-3 right-3">
          <div className="font-display font-bold text-white text-sm line-clamp-1">{s.name}</div>
          <div className="text-[11px] text-[#A1A1AA] mt-0.5">📍 {s.city}</div>
          <div className="mt-1 flex items-baseline gap-1">
            <span className="text-[#D4FF00] font-display font-black text-base">{fmtMoney(s.starts_at, { decimals: 0 })}</span>
            <span className="text-[10px] text-[#A1A1AA]">/day · {s.item_count} pieces</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function HomePage() {
  const { user } = useAuth();
  const [data, setData] = useState({ banners: [], categories: [], trending: { products: [], stores: [], streams: [] }, streams: [], videos: [] });
  const [shopper, setShopper] = useState(null);
  const [forMom, setForMom] = useState([]);

  useEffect(() => {
    (async () => {
      const [banners, cats, tr, streams, videos] = await Promise.all([
        api.get("/banners"), api.get("/categories"),
        api.get("/trending"), api.get("/streams?live_only=true"),
        api.get("/videos"),
      ]);
      setData({
        banners: banners.data, categories: cats.data,
        trending: tr.data, streams: streams.data, videos: videos.data,
      });
    })();
  }, []);

  // Personalized strip: recommend products based on the active shopper's enabled prefs
  useEffect(() => {
    if (!user) { setShopper(null); setForMom([]); return; }
    (async () => {
      try {
        const { data: rec } = await api.get("/buyer/recommended", { params: { limit: 8 } });
        if (!rec?.is_personalized || !rec.products?.length) {
          setShopper(null); setForMom([]); return;
        }
        setShopper({ owner: rec.owner, owner_name: rec.owner_name, filters: rec.filters });
        setForMom(rec.products);
      } catch { setShopper(null); setForMom([]); }
    })();
  }, [user]);

  // AI "For you" rail — embedding-based recommendations using view/save/cart history
  const [forYou, setForYou] = useState([]);
  const [forYouPersonalised, setForYouPersonalised] = useState(false);
  useEffect(() => {
    if (!user) { setForYou([]); setForYouPersonalised(false); return; }
    (async () => {
      try {
        const { data } = await api.get("/recommendations/for-you", { params: { limit: 8 } });
        setForYou(data.items || []);
        setForYouPersonalised(!!data.personalised);
      } catch { setForYou([]); setForYouPersonalised(false); }
    })();
  }, [user]);

  // Rentals rail — nearby rentable stores (clothes, jewelry, etc.).
  // Visible to all (guest + logged-in). Hidden when platform-disabled
  // or no rentable stores exist.
  const [rentalStores, setRentalStores] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/rentals/nearby", { params: { limit: 8 } });
        if (data?.platform_enabled !== false) {
          setRentalStores(data?.stores || []);
        }
      } catch { setRentalStores([]); }
    })();
  }, []);

  const shopperFilterChips = useMemo(() => {
    if (!shopper?.filters) return [];
    const out = [];
    for (const [k, v] of Object.entries(shopper.filters)) {
      if (k === "size" && shopper.filters.sizes) continue; // dedupe
      const labelMap = { sizes: "Size", size: "Size", gender: "For", genders: "For" };
      const label = labelMap[k] || k;
      const display = Array.isArray(v) ? v.join(", ") : v;
      out.push(`${label} ${display}`);
    }
    return out;
  }, [shopper]);

  const firstBanner = data.banners[0];

  // Announcements strip (admin-managed)
  const [announcements, setAnnouncements] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("dismissed_anns") || "[]")); }
    catch { return new Set(); }
  });
  useEffect(() => {
    (async () => {
      try {
        const role = user?.role || "buyer";
        const { data } = await api.get("/announcements/active", { params: { role } });
        setAnnouncements(data);
      } catch { /* ignore */ }
    })();
  }, [user?.role]);
  const dismissAnn = (id) => {
    const next = new Set(dismissed); next.add(id);
    setDismissed(next);
    try { localStorage.setItem("dismissed_anns", JSON.stringify([...next])); } catch {}
  };
  const visibleAnns = announcements.filter(a => !dismissed.has(a.ann_id));

  // Active top-banner ad (one, above-the-fold)
  const [topAd, setTopAd] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/ads", { params: { placement: "home_top", limit: 1 } });
        if (data?.length) setTopAd(data[0]);
      } catch {}
    })();
  }, []);

  // Admin-managed home settings (marquee, section toggles, categories-first toggle)
  const [siteSettings, setSiteSettings] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/site/home-settings");
        setSiteSettings(data);
      } catch {}
    })();
  }, []);
  const sec = siteSettings?.sections || {};
  const showCat = sec.categories !== false;
  const showLive = sec.live !== false;
  const showTrending = sec.trending !== false;
  const showStores = sec.stores !== false;
  const showVideos = sec.videos !== false;
  const showShopperStrip = sec.shopper_strip !== false;
  const showRentals = sec.rentals !== false;
  const catFirst = siteSettings?.categories_first !== false;
  const marqueeLines = (siteSettings?.marquee_lines && siteSettings.marquee_lines.length)
    ? siteSettings.marquee_lines
    : ["FREE SHIPPING WORLDWIDE", "SHOP LIVE 24/7", "30-DAY FREE RETURNS", "SHOPRIZZ"];

  // Build the categories strip (admin-controlled — only active ones; new endpoint also filters)
  const categoryStrip = (showCat && data.categories.length > 0) && (
    <section data-testid="home-categories">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display font-black text-base sm:text-xl uppercase tracking-tight">
          Shop by category<span className="text-[#D4FF00]">.</span>
        </h2>
        <Link to="/search" className="text-xs font-bold text-[#A1A1AA] hover:text-[#D4FF00] flex items-center gap-1">
          See all <ChevronRight size={12}/>
        </Link>
      </div>
      <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 -mx-4 px-4 sm:-mx-0 sm:px-0">
        {data.categories.map((c) => {
          const Icon = Lucide[c.icon] || Lucide.Package;
          return (
            <Link
              key={c.category_id}
              to={`/category/${c.category_id}`}
              data-testid={`cat-${c.category_id}`}
              className="group shrink-0 w-[110px] sm:w-[130px] text-center"
            >
              <div className="aspect-square rounded-2xl overflow-hidden border border-[#3D2845] group-hover:border-[#D4FF00] relative transition">
                {c.image
                  ? <img src={c.image} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition"/>
                  : <div className="w-full h-full bg-[#261534]"/>}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"/>
                <Icon size={20} strokeWidth={2.5} className="absolute top-2 left-2 text-[#D4FF00]"/>
              </div>
              <div className="mt-2 text-xs font-bold line-clamp-2">{c.name}</div>
            </Link>
          );
        })}
      </div>
    </section>
  );

  return (
    <div className="pb-24 md:pb-8">
      {/* CATEGORY STRIP — admin can move it above the hero */}
      {catFirst && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-2">
          {categoryStrip}
        </div>
      )}

      {/* HERO with LIVE NOW overlay */}
      <section className="relative overflow-hidden">
        {firstBanner && (
          <div className="relative min-h-[340px] sm:min-h-[440px] lg:min-h-[520px]">
            <img src={firstBanner.image} alt="" className="absolute inset-0 w-full h-full object-cover"/>
            <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/75 to-black/30"/>
            <div className="absolute inset-0 grain"/>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12">
              <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-[#D4FF00] mb-3">
                <Flame size={12}/> {firstBanner.subtitle}
              </div>
              <h1 className="font-display font-black text-3xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] tracking-tight mb-5 max-w-3xl">
                {firstBanner.title} · <span className="title-gradient">shop the feed.</span>
              </h1>
              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 mb-8 max-w-md sm:max-w-none">
                <Link to={firstBanner.cta_href || "/live"} className="btn-primary inline-flex items-center justify-center gap-2.5 w-full sm:w-auto" data-testid="hero-live-btn">
                  {firstBanner.cta_label || "Shop Live"} <Play size={20} className="fill-black"/>
                </Link>
                <Link to="/go-live"
                  className="inline-flex items-center justify-center gap-2.5 w-full sm:w-auto px-6 py-3 rounded-full font-display font-bold uppercase tracking-widest text-sm text-black bg-[#00F0FF] hover:bg-[#00F0FF]/90 shadow-[0_8px_32px_rgba(0,240,255,0.35)] transition"
                  data-testid="hero-sell-live-btn">
                  <Radio size={20}/> Sell Live
                </Link>
                <Link to="/new-launches"
                  className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-full font-display font-bold uppercase tracking-widest text-sm text-white bg-[#FF007F] hover:bg-[#FF007F]/90 shadow-[0_8px_32px_rgba(255,0,127,0.35)] transition"
                  data-testid="hero-launches-btn">
                  <Rocket size={14}/> New Launches
                </Link>
                {!user && (
                  <Link to="/login" className="btn-secondary text-center w-full sm:w-auto" data-testid="hero-login-btn">Login / Sign up</Link>
                )}
                <Link to="/" className="text-xs text-[#A1A1AA] hover:text-[#D4FF00] underline text-center sm:text-left mt-1 sm:mt-0 sm:ml-2" data-testid="hero-guest-btn">
                  Or browse as guest →
                </Link>
              </div>

              {/* LIVE NOW — inline in hero */}
              {showLive && data.streams.length > 0 && (
                <div className="">
                  <div className="flex items-end justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="live-badge"><span className="dot"/> LIVE NOW</span>
                      <span className="text-xs font-bold uppercase tracking-widest text-[#A1A1AA]">{data.streams.length} streams</span>
                    </div>
                    <Link to="/live" className="text-xs font-bold text-[#FF007F] hover:text-[#D4FF00] flex items-center gap-1" data-testid="hero-live-see-all">
                      See all <ChevronRight size={14}/>
                    </Link>
                  </div>
                  <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x snap-mandatory">
                    {data.streams.slice(0, 8).map(s => (
                      <div key={s.stream_id} className="shrink-0 w-[170px] sm:w-[200px] snap-start">
                        <StreamCard s={s}/>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* MARQUEE — admin-editable lines */}
      <div className="py-1"><div className="w-full bg-[#D4FF00]"></div></div>
      <section className="">
        <div className="overflow-hidden bg-[#D4FF00] border-y-2 border-black py-3" data-testid="home-marquee">
          <div className="marquee-track">
            {Array.from({length: 6}).map((_, i) => (
              <span key={`marquee-rep-${i}`} className="font-display font-black text-black text-sm uppercase tracking-wider px-8">
                {marqueeLines.map((l, j) => (
                  <span key={`marquee-line-${l}-${j}`}>★ {l}{j < marqueeLines.length - 1 ? " · " : "  "}</span>
                ))}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* RENTALS RAIL — appears right after the hero / new-launches CTA.
          Horizontal scroller of nearby ateliers offering clothes,
          jewelry, etc. on rent. Tap → /rentals discovery page. */}
      {showRentals && rentalStores.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8" data-testid="home-rentals">
          <div className="flex items-end justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calendar size={20} className="text-[#00F0FF]"/>
              <h2 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight">
                Rent the look<span className="text-[#00F0FF]">.</span>
              </h2>
              <span className="hidden sm:inline text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA] ml-1">
                · {rentalStores.length} ateliers
              </span>
            </div>
            <Link to="/rentals" data-testid="home-rentals-see-all"
              className="text-sm font-bold text-[#00F0FF] hover:text-[#D4FF00] flex items-center gap-1">
              See all <ChevronRight size={14}/>
            </Link>
          </div>
          <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 -mx-4 px-4 sm:-mx-0 sm:px-0 snap-x snap-mandatory">
            {rentalStores.map(s => <RentalRailCard key={s.store_id} s={s}/>)}
          </div>
        </section>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12 pt-8">

        {/* Categories — only show inline if NOT pinned to top */}
        {!catFirst && categoryStrip}
        {/* Announcements strip */}
        {visibleAnns.length > 0 && (
          <section className="-mt-2 space-y-2" data-testid="home-announcements">
            {visibleAnns.map(a => (
              <div key={a.ann_id}
                data-testid={`home-announcement-${a.ann_id}`}
                className={`card p-3 sm:p-4 flex items-start gap-3 border-l-4 ${
                  a.severity === "promo"   ? "border-[#D4FF00]" :
                  a.severity === "warning" ? "border-[#FF9B00]" :
                  a.severity === "success" ? "border-[#00FF88]" :
                                             "border-[#00F0FF]"
                }`}>
                <Sparkles size={18} className="shrink-0 mt-0.5 text-[#D4FF00]"/>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm">{a.title}</div>
                  <div className="text-xs text-[#A1A1AA]">{a.body}</div>
                  {a.link_url && <Link to={a.link_url} className="text-xs text-[#D4FF00] hover:underline mt-1 inline-block">
                    Learn more →</Link>}
                </div>
                <button onClick={() => dismissAnn(a.ann_id)}
                  data-testid={`dismiss-ann-${a.ann_id}`}
                  aria-label="Dismiss"
                  className="text-[#52525B] hover:text-[#FAFAFA] p-1"><X size={14}/></button>
              </div>
            ))}
          </section>
        )}

        {/* Top banner ad */}
        {topAd && (
          <section data-testid="home-top-ad" className="-mt-2">
            <a href={topAd.link_url || "#"}
              onClick={() => api.post(`/ads/${topAd.ad_id}/click`).catch(() => {})}
              target={topAd.link_url?.startsWith("http") ? "_blank" : undefined}
              rel="noreferrer"
              className="block card overflow-hidden hover:border-[#D4FF00] transition">
              {topAd.kind === "video" ? (
                <video src={topAd.media_url} poster={topAd.thumbnail_url || undefined}
                  autoPlay muted loop playsInline className="w-full h-40 object-cover"/>
              ) : (
                <img src={topAd.media_url} alt={topAd.title}
                  className="w-full h-40 object-cover"/>
              )}
              <div className="px-4 py-2 flex items-center justify-between text-xs">
                <div><span className="font-bold">{topAd.title}</span>
                  {topAd.sponsor_name && <span className="text-[#A1A1AA] ml-2">· Sponsored by {topAd.sponsor_name}</span>}</div>
                <span className="text-[10px] uppercase tracking-widest text-[#52525B] font-bold">Ad</span>
              </div>
            </a>
          </section>
        )}

        {/* Personalised strip — Shopping for {Name} */}
        {showShopperStrip && shopper && forMom.length > 0 && (
          <section data-testid="home-shopper-strip">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Sparkles size={18} className="text-[#D4FF00]"/>
                <h2 className="font-display font-black text-2xl sm:text-3xl uppercase">
                  For <span className="text-[#D4FF00]">{shopper.owner_name}</span>
                </h2>
                {shopperFilterChips.map((c, i) => (
                  <span key={`${c}-${i}`} className="chip text-[10px]">{c}</span>
                ))}
              </div>
              <Link to="/search" className="text-xs text-[#A1A1AA] hover:text-[#D4FF00] flex items-center gap-1">
                See all <ChevronRight size={12}/>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {forMom.map((p, i) => <ProductCard key={p.product_id} p={p} index={i}/>)}
            </div>
          </section>
        )}

        {/* AI "For you" — embedding-based personalised picks */}
        {user && forYouPersonalised && forYou.length > 0 && (
          <section data-testid="home-for-you">
            <div className="flex items-end justify-between mb-4">
              <div className="flex items-center gap-2">
                <Sparkles size={18} className="text-[#FF007F]"/>
                <h2 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight">
                  For <span className="text-[#FF007F]">you</span>
                </h2>
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA] hidden sm:inline ml-1">
                  · AI picks
                </span>
              </div>
              <Link to="/search" className="text-sm font-bold text-[#A1A1AA] hover:text-[#FF007F] flex items-center gap-1">
                See all <ChevronRight size={14}/>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {forYou.slice(0, 8).map((p, i) => <ProductCard key={p.product_id} p={p} index={i}/>)}
            </div>
          </section>
        )}

        {/* Trending products */}
        {showTrending && (
          <section data-testid="home-trending">
            <SectionHeader title="Trending everywhere" href="/search"/>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {data.trending.products.slice(0, 8).map((p, i) => <ProductCard key={p.product_id} p={p} index={i}/>)}
            </div>
          </section>
        )}

        {/* Stores */}
        {showStores && (
          <section data-testid="home-stores">
            <SectionHeader title="Top stores"/>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
              {data.trending.stores.slice(0, 6).map((s) => <StoreCard key={s.store_id} s={s}/>)}
            </div>
          </section>
        )}

        {/* Videos */}
        {showVideos && data.videos.length > 0 && (
          <section data-testid="home-videos">
            <SectionHeader title="Shoppable videos" href="/videos"/>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              {data.videos.slice(0, 4).map((v) => (
                <Link to="/videos" key={v.video_id} data-testid={`video-${v.video_id}`} className="card cursor-pointer block">
                  <div className="aspect-video relative overflow-hidden">
                    <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover"/>
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <div className="w-12 h-12 rounded-full bg-[#D4FF00] flex items-center justify-center">
                        <Play size={18} className="text-black fill-black"/>
                      </div>
                    </div>
                  </div>
                  <div className="p-3 text-sm font-semibold line-clamp-1">{v.title}</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Footer CTA */}
        <section className="rounded-3xl bg-[#261534] border border-[#3D2845] p-8 sm:p-12 overflow-hidden relative grain">
          <h2 className="font-display font-black text-3xl sm:text-5xl uppercase">Sell live. <span className="text-[#D4FF00]">Go global.</span></h2>
          <p className="text-[#A1A1AA] mt-3 max-w-xl">Open your store in minutes. Go live when you want. Reach a Gen-Z audience across 13 categories.</p>
          <Link to="/register" className="btn-primary mt-6 inline-block" data-testid="cta-become-seller">Become a seller</Link>
        </section>
      </div>
    </div>
  );
}

function SectionHeader({ title, href, accent = "green" }) {
  return (
    <div className="flex items-end justify-between mb-4">
      <h2 className={`font-display font-black text-2xl sm:text-3xl uppercase tracking-tight`}>
        {title}
        <span className={accent==="pink" ? "text-[#FF007F]" : "text-[#D4FF00]"}>.</span>
      </h2>
      {href && (
        <Link to={href} className="text-sm font-bold text-[#A1A1AA] hover:text-[#D4FF00] flex items-center gap-1">
          See all <ChevronRight size={14}/>
        </Link>
      )}
    </div>
  );
}
